import { UnwrapPromise } from "@prisma/client";
import { LoaderFunction, useLoaderData } from "remix";
import { EditButtonLink } from "~/components/Button";
import { ContentBlock } from "~/components/ContentBlock";
import { Human } from "~/components/Human";
import { ContentBlockModel, getContentBlock } from "~/utils/contentBlocks";
import { db } from "~/utils/db.server";
import { requireFestival } from "~/utils/festival.server";
import { getUserId } from "~/utils/session.server";
import { useIsLoggedIn } from "~/utils/useIsLoggedIn";

interface LoaderData {
  title: ContentBlockModel;
  content: ContentBlockModel;
  editable: boolean;
  humans: UnwrapPromise<ReturnType<typeof loadHumans>>;
}

function loadHumans(festivalId: string) {
  return db.human.findMany({
    where: { festivalId },
    orderBy: { name: "asc" },
    include: { image: true },
  });
}

export const loader: LoaderFunction = async ({ request, params }) => {
  const [festival, title, content, userId] = await Promise.all([
    requireFestival(params.festivalSlug),
    getContentBlock(`menschen-${params.festivalSlug}/title`),
    getContentBlock(`menschen-${params.festivalSlug}/content`),
    getUserId(request),
  ]);

  const humans = await loadHumans(festival.id);

  return {
    title,
    content,
    editable: userId !== null,
    humans,
  };
};

export default function DigitaleStaende() {
  const { title, content, editable, humans } = useLoaderData<LoaderData>();
  const isLoggedIn = useIsLoggedIn();

  return (
    <>
      <div className="flex-grow w-full my-10">
        <h1 className="text-5xl font-bold mb-3">
          <ContentBlock block={title} editable={editable} />
        </h1>

        <ContentBlock block={content} editable={editable} />
      </div>
      <div className="main-layout-full-bleed bg-gray-50 py-10 border-t border-t-gray-200">
        <div className="main-layout-centered">
          <ul className="py-20 list-reset grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {humans.map((human) => (
              <li key={human.id} className="mb-24">
                <Human {...human} />
                {isLoggedIn && (
                  <EditButtonLink to={`${human.id}/edit`}>Edit</EditButtonLink>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
      {isLoggedIn && (
        <div className="my-4">
          <EditButtonLink to="new">Add Human</EditButtonLink>
        </div>
      )}
    </>
  );
}
